import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { LoadingManager } from '../utils/LoadingManager';
import { CollisionSystem } from '../utils/CollisionSystem';
import { createCoffeeMachinePrimitive } from './CoffeeMachinePrimitive';
import { isMobile } from '../utils/deviceDetection'; // Device detection utility
import { addClawMachineToRoom } from '../models/ClawMachine'; // Import the claw machine
import { musicPlayer } from '../utils/MusicPlayer';
import { Vector3 } from 'three';
import { VendingMachineUI } from './VendingMachineUI';

// Arcade configuration types
interface ArcadeGame {
  url?: string;
  image?: string;
  name: string;
}

interface WallConfig {
  right: ArcadeGame[];
  left: ArcadeGame[];
}

// PC configuration types
interface PCConfig {
  screenImage: string;
  url: string;
  name: string;
}

type PCConfigMap = {
  [key: string]: PCConfig;
}

// Define the interface for game configuration from JSON
interface GameConfig {
  id: string;
  name: string;
  url: string;
  image: string;
  scoringMethod: 'time' | 'game';
  description: string;
}

// Interface for the games data loaded from JSON
interface GamesData {
  games: GameConfig[];
}

// Default arcade configuration - this will be replaced with loaded games
let DEFAULT_ARCADE_CONFIG: WallConfig = {
  right: [
    { url: 'https://fly.pieter.com', image: '/assets/fly.pieter.png', name: 'Fly Pieter' },
    { url: 'https://vibesail.com', image: '/assets/vibe.sail.png', name: 'Vibe Sail' },
    { url: 'https://www.hotairvibe.com/', image: '/assets/hotairvibe.png', name: 'Hot Air Vibe' },
    { url: './minigames/tetris.html', image: '/assets/tetris.png', name: 'Tetris' },
    { url: './minigames/pacman.html', image: '/assets/pacman.png', name: 'Pacman' }
  ],
  left: [
    { url: './minigames/pong.html', image: '/assets/pong.svg', name: 'Pong' },
    { url: './minigames/space_defenders.html', image: '/assets/space_defenders.svg', name: 'Space Defenders' },
    { url: './minigames/snake.html', image: '/assets/snake.svg', name: 'Snake' },
    { url: './minigames/2048.html', image: '/assets/2048.svg', name: '2048' },
    { url: './minigames/sudoku.html', image: '/assets/sudoku.svg', name: 'Sudoku' }
  ]
};

// Function to load games from the JSON configuration
async function loadGamesConfig(): Promise<void> {
  try {
    const response = await fetch('/minigames/games.json');
    if (!response.ok) {
      throw new Error(`Failed to load games config: ${response.status} ${response.statusText}`);
    }
    const data: GamesData = await response.json();
    
    // Transform game configs to arcade config format
    const rightWallGames = data.games.map(game => ({
      url: game.url,
      image: game.image,
      name: game.name
    }));
    
    // Update the default arcade config with the loaded games
    DEFAULT_ARCADE_CONFIG = {
      right: rightWallGames,
      left: DEFAULT_ARCADE_CONFIG.left // Keep the left wall the same
    };
    
    console.log('Loaded games for arcade machines:', rightWallGames);
  } catch (error) {
    console.error('Error loading games configuration for arcade machines:', error);
    // Keep using the default config if loading fails
  }
}

// Load the games configuration when this module is imported
loadGamesConfig().catch(error => {
  console.error('Failed to load games config:', error);
});

// Default PC configuration
const DEFAULT_PC_CONFIG: PCConfigMap = {
  "0": { 
    screenImage: '/assets/github.png',
    url: 'https://github.com',
    name: 'GitHub'
  },
  "1": {
    screenImage: '/assets/stackoverflow.png',
    url: 'https://stackoverflow.com',
    name: 'Stack Overflow'
  },
  "2": {
    screenImage: '/assets/mdn.png',
    url: 'https://developer.mozilla.org',
    name: 'MDN'
  },
  "3": {
    screenImage: '/assets/threejs.png',
    url: 'https://threejs.org',
    name: 'Three.js'
  },
  "4": {
    screenImage: '/assets/react.png',
    url: 'https://reactjs.org',
    name: 'React'
  },
  "5": {
    screenImage: '/assets/nodejs.png',
    url: 'https://nodejs.org',
    name: 'Node.js'
  },
  "6": {
    screenImage: '/assets/typescript.png',
    url: 'https://www.typescriptlang.org',
    name: 'TypeScript'
  },
  "7": {
    screenImage: '/assets/vscode.png',
    url: 'https://code.visualstudio.com',
    name: 'VS Code'
  },
  "8": {
    screenImage: '/assets/webpack.png',
    url: 'https://webpack.js.org',
    name: 'Webpack'
  },
  "9": {
    screenImage: '/assets/babel.png',
    url: 'https://babeljs.io',
    name: 'Babel'
  },
  "10": {
    screenImage: '/assets/npm.png',
    url: 'https://www.npmjs.com',
    name: 'npm'
  },
  "11": {
    screenImage: '/assets/git.png',
    url: 'https://git-scm.com',
    name: 'Git'
  }
};

// Global variable to store the current arcade configuration
let ARCADE_CONFIG: WallConfig = DEFAULT_ARCADE_CONFIG;

// Add these constants at the top of the file
const GEOMETRY_SEGMENTS = isMobile() ? 4 : 16;
const MAX_LIGHTS = isMobile() ? 2 : 8;
const SHADOW_ENABLED = !isMobile();
const EMISSIVE_INTENSITY = isMobile() ? 0.2 : 0.5;
const TEXTURE_SIZE = isMobile() ? 256 : 512;
const MAX_ANISOTROPY = isMobile() ? 1 : 4;
const USE_INSTANCING = true;
const DRAW_DISTANCE = isMobile() ? 15 : 30;
const LOD_DISTANCES = isMobile() ? [5, 10] : [10, 20];

// Create shared materials and geometries for instancing
const sharedMaterials = {
  wall: new THREE.MeshStandardMaterial({ 
    color: 0x2b1b3a,
    roughness: 0.9,
    metalness: 0.1,
    side: THREE.DoubleSide
  }),
  desk: new THREE.MeshStandardMaterial({ 
    color: 0x1a1a1a,
    roughness: 0.7,
    metalness: 0.3
  }),
  emissive: new THREE.MeshStandardMaterial({
    color: 0x9932cc,
    emissive: 0x9932cc,
    emissiveIntensity: EMISSIVE_INTENSITY
  })
};

// Create shared geometries
const sharedGeometries = {
  wall: new THREE.BoxGeometry(30, 8, 0.2, 1, 1, 1),
  desk: new THREE.BoxGeometry(2.2, 0.8, 1.4, 1, 1, 1),
  panel: new THREE.PlaneGeometry(0.8, 0.8, 1, 1)
};

// Texture loader with caching
const textureLoader = new THREE.TextureLoader();
const textureCache = new Map<string, THREE.Texture>();

function loadCachedTexture(url: string): Promise<THREE.Texture> {
  if (textureCache.has(url)) {
    return Promise.resolve(textureCache.get(url)!);
  }
  
  return new Promise((resolve, reject) => {
    textureLoader.load(
      url,
      (texture) => {
        texture.generateMipmaps = true;
        texture.minFilter = THREE.LinearMipMapLinearFilter;
        texture.anisotropy = MAX_ANISOTROPY;
        texture.colorSpace = THREE.SRGBColorSpace;
        if (isMobile()) {
          const canvas = document.createElement('canvas');
          canvas.width = TEXTURE_SIZE;
          canvas.height = TEXTURE_SIZE;
          const ctx = canvas.getContext('2d')!;
          ctx.drawImage(texture.image, 0, 0, TEXTURE_SIZE, TEXTURE_SIZE);
          texture.image = canvas;
          texture.needsUpdate = true;
        }
        textureCache.set(url, texture);
        resolve(texture);
      },
      undefined,
      reject
    );
  });
}

// Function to create LOD version of a mesh
function createLODMesh(highDetail: THREE.Mesh, mediumDetail: THREE.Mesh, lowDetail: THREE.Mesh): THREE.LOD {
  const lod = new THREE.LOD();
  lod.addLevel(highDetail, 0);
  lod.addLevel(mediumDetail, LOD_DISTANCES[0]);
  lod.addLevel(lowDetail, LOD_DISTANCES[1]);
  return lod;
}

// Function to update arcade configuration
export function updateArcadeConfig(newConfig: Partial<WallConfig>) {
  ARCADE_CONFIG = {
    right: [...(newConfig.right || DEFAULT_ARCADE_CONFIG.right)],
    left: [...(newConfig.left || DEFAULT_ARCADE_CONFIG.left)]
  };
}

// Define an interface for interactive objects
interface InteractiveObject {
  mesh: THREE.Object3D;
  url: string;
  label: string;
  interactionDistance: number;
  onInteract?: () => void;
}

// Character controller interface
interface CharacterControllerBase {
  startDancing: () => void;
  stopDancing: () => void;
  getIsDancing: () => boolean;
  getCameraHorizontalAngle: () => number;
}

interface MultiplayerManager {
  //forceDanceUpdate?: (isDancing: boolean) => void;
  getPlayerScore: () => number;
  addPoints: (points: number) => void;
  startThirdPartyGameScoring: () => void;
  stopThirdPartyGameScoring: () => void;
}

// Update the Window interface
declare global {
  interface Window {
    registerInteractiveObject: (mesh: THREE.Object3D, url: string, label: string) => void;
    checkInteractiveObjects: (playerPosition: THREE.Vector3) => void;
    updateArcadeConfig: (config: Partial<WallConfig>) => void;
    character?: {
      controller: CharacterControllerBase;
      position?: THREE.Vector3;
    };
    multiplayerManager?: MultiplayerManager;
    danceMachineInteractionBox?: THREE.Mesh;
    lastPositionLog?: number;
    playerName?: string;
    interactiveObjects: InteractiveObject[];
    handleInteraction: () => void;
    openVendingMachine?: () => void;
  }
}

// Global array to store all interactive objects
window.interactiveObjects = [];

// Function to add dance machine to the room
async function addDanceMachineToRoom(
  cafeGroup: THREE.Group,
  collisionSystem: CollisionSystem
): Promise<THREE.Group> {
  const danceMachineGroup = new THREE.Group();
  danceMachineGroup.name = 'danceMachine';
  
  // Load the GLB model
  return new Promise((resolve, reject) => {
    const loader = new GLTFLoader();
    loader.load(
      '/assets/dance_machine.glb',
      (gltf) => {
        const model = gltf.scene;
        
        // Scale the model appropriately
        model.scale.set(0.8, 0.8, 0.8);
        
        // Add the model to the group
        danceMachineGroup.add(model);
        
        // Position the dance machine next to the claw machine
        danceMachineGroup.position.set(-20, 0, -12); // 2 units to the right of claw machine
        // Facing the same direction as claw machine
        danceMachineGroup.rotation.y = 0;
        
        // Add to the cafe group
        cafeGroup.add(danceMachineGroup);
        
        // Create a collision box
        const interactionBox = new THREE.Mesh(
          new THREE.BoxGeometry(2, 3, 2), // Made box bigger for easier interaction
          new THREE.MeshBasicMaterial({ visible: false })
        );
        // Adjust interaction box position to the right of the dance machine
        interactionBox.position.set(-6.8, 2, -14); // Moved right from original position
        interactionBox.name = 'danceMachineInteraction';
        
        // Add to the cafe group
        cafeGroup.add(interactionBox);
        
        // Add collision detection
        if (collisionSystem) {
          collisionSystem.addCollider(interactionBox);
        }
        
        // Register for interaction
        if (window.registerInteractiveObject) {
          window.registerInteractiveObject(
            interactionBox,
            "#dance", // Use hash URL to keep interaction local
            "Press E to Dance!"
          );
        }
        
        // Also add directly to interactiveObjects array for more reliable detection
        window.interactiveObjects.push({
          mesh: interactionBox,
          url: "#dance",
          label: "Press E to Dance!",
          interactionDistance: 1.5  // Significantly reduced for much closer interaction
        });
        
        // Make sure materials and meshes are properly visible
        model.traverse((child: THREE.Object3D) => {
          if (child instanceof THREE.Mesh) {
            child.castShadow = true;
            child.receiveShadow = true;
            
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material.forEach(mat => {
                  mat.transparent = false;
                  mat.opacity = 1;
                  if (mat.map) {
                    mat.map.colorSpace = THREE.SRGBColorSpace;
                  }
                });
              } else {
                child.material.transparent = false;
                child.material.opacity = 1;
                if (child.material.map) {
                  child.material.map.colorSpace = THREE.SRGBColorSpace;
                }
              }
            }
          }
        });
        
        resolve(danceMachineGroup);
      },
      undefined,
      reject
    );
  });
}

// Create a glowing Vibeverse Portal
function createVibeversePortal(scene: THREE.Scene, collisionSystem: CollisionSystem): THREE.Object3D {
  // Create a portal group
  const portalGroup = new THREE.Group();
  
  // Create the portal frame (a torus)
  const torusGeometry = new THREE.TorusGeometry(1.5, 0.3, 16, 32);
  const torusMaterial = new THREE.MeshStandardMaterial({ 
    color: 0x4287f5,
    emissive: 0x4287f5,
    emissiveIntensity: 0.8,
    metalness: 0.7,
    roughness: 0.3
  });
  const torus = new THREE.Mesh(torusGeometry, torusMaterial);
  torus.rotation.y = Math.PI / 2; // Make the portal face forward
  portalGroup.add(torus);
  
  // Create the portal inner effect (a disc)
  const discGeometry = new THREE.CircleGeometry(1.4, 32);
  const discMaterial = new THREE.MeshBasicMaterial({ 
    color: 0x00ffff,
    transparent: true,
    opacity: 0.7,
    side: THREE.DoubleSide
  });
  const disc = new THREE.Mesh(discGeometry, discMaterial);
  disc.rotation.y = Math.PI / 2; // Make the portal face forward
  portalGroup.add(disc);
  
  // Add swirling particle effect
  const particlesGeometry = new THREE.BufferGeometry();
  const particleCount = 100;
  const particlePositions = new Float32Array(particleCount * 3);
  
  for (let i = 0; i < particleCount; i++) {
    // Generate positions in a disc pattern
    const angle = Math.random() * Math.PI * 2;
    const radius = Math.random() * 1.3;
    particlePositions[i * 3] = 0; // X (we'll animate this)
    particlePositions[i * 3 + 1] = Math.sin(angle) * radius; // Y
    particlePositions[i * 3 + 2] = Math.cos(angle) * radius; // Z
  }
  
  particlesGeometry.setAttribute('position', new THREE.BufferAttribute(particlePositions, 3));
  
  const particlesMaterial = new THREE.PointsMaterial({
    color: 0xffffff,
    size: 0.05,
    transparent: true,
    opacity: 0.8
  });
  
  const particles = new THREE.Points(particlesGeometry, particlesMaterial);
  portalGroup.add(particles);
  
  // Add a text label above the portal
  const textTexture = createTextTexture('Vibeverse Portal', {
    fontFace: 'Arial',
    fontSize: 32,
    textColor: '#ffffff',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    padding: 10,
    borderRadius: 5
  });
  
  const textPlaneMaterial = new THREE.MeshBasicMaterial({
    map: textTexture,
    transparent: true,
    opacity: 1,
    side: THREE.DoubleSide
  });
  
  const textPlaneGeometry = new THREE.PlaneGeometry(2, 0.5);
  const textPlane = new THREE.Mesh(textPlaneGeometry, textPlaneMaterial);
  textPlane.position.set(0, 2.2, 0);
  portalGroup.add(textPlane);
  
  // Add collision for the portal
  const portalCollider = new THREE.Mesh(
    new THREE.CylinderGeometry(1.4, 1.4, 0.2, 16),
    new THREE.MeshBasicMaterial({ visible: false })
  );
  portalCollider.rotation.x = Math.PI / 2;
  
  // Add the collider to the portal group
  portalGroup.add(portalCollider);
  
  // Register the portal with the interaction system
  const portalUrl = '#vibeverse-portal';
  if (window.registerInteractiveObject) {
    window.registerInteractiveObject(portalCollider, portalUrl, 'Enter Vibeverse Portal');
  }
  
  // Also add directly to interactiveObjects array
  window.interactiveObjects.push({
    mesh: portalCollider,
    url: portalUrl,
    label: 'Enter Vibeverse Portal',
    interactionDistance: 2
  });
  
  // Animate the portal
  function animatePortal() {
    // Rotate the torus
    torus.rotation.z += 0.005;
    
    // Animate the particles
    const positions = particlesGeometry.getAttribute('position');
    for (let i = 0; i < particleCount; i++) {
      // Make particles swirl around the portal center
      const x = positions.getX(i);
      const y = positions.getY(i);
      const z = positions.getZ(i);
      
      // Update X position for swirling effect
      const time = Date.now() * 0.001;
      positions.setX(i, Math.sin(time + i * 0.1) * 0.05);
      
      // Slowly rotate particles
      const angle = Math.atan2(z, y) + 0.01;
      const radius = Math.sqrt(y * y + z * z);
      positions.setY(i, Math.cos(angle) * radius);
      positions.setZ(i, Math.sin(angle) * radius);
    }
    positions.needsUpdate = true;
    
    // Pulse the disc
    const scale = 0.9 + Math.sin(Date.now() * 0.002) * 0.1;
    disc.scale.set(scale, scale, scale);
    
    requestAnimationFrame(animatePortal);
  }
  
  animatePortal();
  
  return portalGroup;
}

// Helper function to create a text texture
function createTextTexture(text: string, options: {
  fontFace?: string;
  fontSize?: number;
  textColor?: string;
  backgroundColor?: string;
  padding?: number;
  borderRadius?: number;
}): THREE.Texture {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  
  if (!context) {
    throw new Error('Unable to get 2D context');
  }
  
  // Set default options
  const {
    fontFace = 'Arial',
    fontSize = 24,
    textColor = '#ffffff',
    backgroundColor = 'rgba(0, 0, 0, 0.8)',
    padding = 8,
    borderRadius = 0
  } = options;
  
  // Set font for measuring
  context.font = `${fontSize}px ${fontFace}`;
  
  // Measure text width
  const textMetrics = context.measureText(text);
  const textWidth = textMetrics.width;
  
  // Set canvas dimensions based on text size and padding
  canvas.width = textWidth + padding * 2;
  canvas.height = fontSize + padding * 2;
  
  // Reset font after resize (canvas resize resets context)
  context.font = `${fontSize}px ${fontFace}`;
  
  // Draw background with rounded corners if specified
  context.fillStyle = backgroundColor;
  if (borderRadius > 0) {
    roundRect(context, 0, 0, canvas.width, canvas.height, borderRadius);
    context.fill();
  } else {
    context.fillRect(0, 0, canvas.width, canvas.height);
  }
  
  // Draw text
  context.fillStyle = textColor;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(text, canvas.width / 2, canvas.height / 2);
  
  // Create texture from canvas
  const texture = new THREE.Texture(canvas);
  texture.needsUpdate = true;
  
  return texture;
}

// Helper function to draw rounded rectangle
function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.arcTo(x + width, y, x + width, y + radius, radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.arcTo(x + width, y + height, x + width - radius, y + height, radius);
  ctx.lineTo(x + radius, y + height);
  ctx.arcTo(x, y + height, x, y + height - radius, radius);
  ctx.lineTo(x, y + radius);
  ctx.arcTo(x, y, x + radius, y, radius);
  ctx.closePath();
}

async function addVendingMachineToRoom(
  cafeGroup: THREE.Group,
  collisionSystem: CollisionSystem
): Promise<THREE.Group> {
  const vendingMachineGroup = new THREE.Group();
  vendingMachineGroup.name = 'vendingMachine';

  const loader = new GLTFLoader();
  try {
    const gltf = await loader.loadAsync('/assets/vending_machine.glb');
    const model = gltf.scene;
    
    // Scale and position the model
    model.scale.set(1.5, 1.5, 1.5);
    model.position.set(9, 0, -14); // Position to the right of the claw machine
    model.rotation.y = 0; // Face forward
    
    vendingMachineGroup.add(model);
    
    // Add collision box for the vending machine
    const box = new THREE.Box3().setFromObject(model);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    
    const collisionBox = new THREE.Mesh(
      new THREE.BoxGeometry(size.x, size.y, size.z),
      new THREE.MeshBasicMaterial({ visible: false })
    );
    collisionBox.position.copy(center);
    collisionBox.userData.isCollider = true;
    
    vendingMachineGroup.add(collisionBox);
    collisionSystem.addCollider(collisionBox);
    
    // We'll create a new VendingMachineUI instance with each interaction
    // Add to interactive objects array with custom handler
    window.interactiveObjects.push({
      mesh: collisionBox,
      url: '#vending-machine',
      label: 'Press E to browse toys',
      interactionDistance: 2,
      onInteract: () => {
        console.log('Vending machine interaction triggered');
        VendingMachineUI.getInstance().show();
      }
    });
    
    // Make the vending machine UI accessible through a global method for debugging
    window.openVendingMachine = () => {
      console.log('Manually opening vending machine UI');
      VendingMachineUI.getInstance().show();
    };
    
    cafeGroup.add(vendingMachineGroup);
  } catch (error) {
    console.error('Error loading vending machine model:', error);
  }
  
  return vendingMachineGroup;
}

export async function createCafe(
  scene: THREE.Scene, 
  loadingManager: LoadingManager,
  collisionSystem: CollisionSystem
): Promise<THREE.Group> {
  const cafeGroup = new THREE.Group();
  cafeGroup.name = 'cafe';
  
  // Start background music with a comfortable volume
  musicPlayer.setVolume(0.3);
  musicPlayer.play();
  
  // Create floor with a darker gaming theme
  const floorGeometry = new THREE.PlaneGeometry(30, 30);
  
  // Load floor texture
  const floorTexture = textureLoader.load('/assets/checkerboard_floor.png');
  floorTexture.wrapS = THREE.RepeatWrapping;
  floorTexture.wrapT = THREE.RepeatWrapping;
  floorTexture.repeat.set(6, 6); // Adjust the repeat values to control the tile size
  floorTexture.colorSpace = THREE.SRGBColorSpace;
  floorTexture.anisotropy = MAX_ANISOTROPY;
  
  const floorMaterial = new THREE.MeshStandardMaterial({ 
    map: floorTexture,
    color: 0xffffff, // White color to preserve texture details
    roughness: 0.3, // Lower roughness for a more polished look
    metalness: 0.4, // Higher metalness for more reflectivity
    envMapIntensity: 0.8, // Enhance environment reflections
  });
  
  const floor = new THREE.Mesh(floorGeometry, floorMaterial);
  floor.rotation.x = -Math.PI / 2;
  floor.receiveShadow = true;
  floor.name = 'floor';
  cafeGroup.add(floor);
  collisionSystem.addCollider(floor);
  
  // Create walls with a modern gaming theme
  const wallMaterial = new THREE.MeshStandardMaterial({ 
    color: 0x2b1b3a, // Dark purple
    roughness: 0.9,
    metalness: 0.1
  });
  
  // Create all walls (now taller)
  const walls = createWalls(wallMaterial);
  walls.forEach(wall => {
    cafeGroup.add(wall);
    collisionSystem.addCollider(wall);
  });
  
  // Create ceiling with grid pattern
  const ceilingHeight = 8; // Increased height
  const ceiling = createGridCeiling(ceilingHeight);
  cafeGroup.add(ceiling);

  // Create two rows of back-to-back computer desks
  const computerRows = await createComputerRows();
  computerRows.position.set(0, 0, 0);
  cafeGroup.add(computerRows);
  collisionSystem.addCollider(computerRows);

  // Create arcade machines along the walls
  const arcadeMachines = createArcadeMachines();
  cafeGroup.add(arcadeMachines);
  
  // We'll add colliders for arcade machines after they're loaded

  // Create cafe counter

  // Create barista counter at the front entrance
  const baristaCounter = await createBaristaCounter();
  baristaCounter.position.set(5, 0, 11); // Moved slightly away from the wall (from 14 to 13)
  cafeGroup.add(baristaCounter);
  collisionSystem.addCollider(baristaCounter);

  // Add first coffee machine
  const coffeeMachine1 = await createCoffeeMachine(
    new THREE.Vector3(4.5, 1.4, 13),
    Math.PI
  );
  cafeGroup.add(coffeeMachine1);
  
  // Register first coffee machine for interaction (only in local array)
  window.interactiveObjects.push({
    mesh: coffeeMachine1,
    url: 'https://buymeacoffee.com/xrisdev',
    label: 'Press E to buy me a coffee',
    interactionDistance: 2
  });

  const baristaCounter2 = await createBaristaCounter();
  baristaCounter2.position.set(-5, 0, 10.3);
  baristaCounter2.rotation.y = Math.PI/2;
  cafeGroup.add(baristaCounter2);
  collisionSystem.addCollider(baristaCounter2);

  // Add second coffee machine
  const coffeeMachine2 = await createCoffeeMachine(
    new THREE.Vector3(-3, 1.4, 10.7),
    Math.PI + Math.PI/2
  );
  cafeGroup.add(coffeeMachine2);

  // Register second coffee machine for interaction (only in local array)
  window.interactiveObjects.push({
    mesh: coffeeMachine2,
    url: 'https://buymeacoffee.com/xrisdev',
    label: 'Press E to buy me a coffee',
    interactionDistance: 2
  });

  // Add "AI Cafe" sign on the front wall
  const cafeSign = createAICafeSign();
  cafeSign.position.set(0, 5, 14.9); // Position it on the front wall
  cafeSign.rotation.x = Math.PI * 0.05; // Tilt it slightly downward
  cafeSign.rotation.y = Math.PI; // Rotate it 180 degrees so it faces inside the cafe
  cafeGroup.add(cafeSign);

 


  // Add gaming decorations
  addGamingDecorations(cafeGroup, ceilingHeight);
  
  // Add claw machine and vending machine
  await addClawMachineToRoom(cafeGroup, collisionSystem);
  await addVendingMachineToRoom(cafeGroup, collisionSystem);
  
  // Add dance machine next to the claw machine
  await addDanceMachineToRoom(cafeGroup, collisionSystem);
  
  // Add the Vibeverse Portal in a corner of the room
  const vibePortal = createVibeversePortal(scene, collisionSystem);
  vibePortal.position.set(-10, 2, 13); // Position in a corner of the room
  vibePortal.rotation.y = Math.PI/2;
  scene.add(vibePortal);
  
  scene.add(cafeGroup);
  
  // Add a listener to add colliders for arcade machines once they're loaded
  const checkForArcadeMachines = () => {
    arcadeMachines.traverse((child) => {
      if (child.name === 'arcadeCabinet' && !child.userData.colliderAdded) {
        collisionSystem.addCollider(child);
        child.userData.colliderAdded = true;
      }
    });
    
    // Check again in a second if not all arcade machines are loaded yet
    if (arcadeMachines.children.length < 10) { // 5 machines on each wall
      setTimeout(checkForArcadeMachines, 1000);
    }
  };
  
  // Start checking for arcade machines
  setTimeout(checkForArcadeMachines, 1000);
  
  // Setup interaction system
  setupInteractionSystem();
  
  return cafeGroup;
}

function createWalls(material: THREE.Material): THREE.Mesh[] {
  if (USE_INSTANCING) {
    const wallGeometry = sharedGeometries.wall;
    const wallMaterial = material;
    const instancedWalls = new THREE.InstancedMesh(wallGeometry, wallMaterial, 4);
    
    const matrix = new THREE.Matrix4();
    const wallHeight = 8;
    
    // Back wall
    matrix.makeTranslation(0, wallHeight/2, -15);
    instancedWalls.setMatrixAt(0, matrix);
    
    // Front wall
    matrix.makeTranslation(0, wallHeight/2, 15);
    instancedWalls.setMatrixAt(1, matrix);
    
    // Left wall
    matrix.makeRotationY(Math.PI/2);
    matrix.setPosition(-15, wallHeight/2, 0);
    instancedWalls.setMatrixAt(2, matrix);
    
    // Right wall
    matrix.setPosition(15, wallHeight/2, 0);
    instancedWalls.setMatrixAt(3, matrix);
    
    instancedWalls.instanceMatrix.needsUpdate = true;
    return [instancedWalls];
  }
  
  const walls: THREE.Mesh[] = [];
  const wallHeight = 8; // Increased height
  
  // Back wall
  const backWall = new THREE.Mesh(
    new THREE.BoxGeometry(30, wallHeight, 0.2),
    material
  );
  backWall.position.set(0, wallHeight/2, -15);
  backWall.castShadow = true;
  backWall.receiveShadow = true;
  walls.push(backWall);
  
  // Front wall (now a single continuous wall)
  const frontWall = new THREE.Mesh(
    new THREE.BoxGeometry(30, wallHeight, 0.2),
    material
  );
  frontWall.position.set(0, wallHeight/2, 15);
  frontWall.castShadow = true;
  frontWall.receiveShadow = true;
  walls.push(frontWall);
  
  // Side walls
  const sideWallGeometry = new THREE.BoxGeometry(0.2, wallHeight, 30);
  
  const leftWall = new THREE.Mesh(sideWallGeometry, material);
  leftWall.position.set(-15, wallHeight/2, 0);
  leftWall.castShadow = true;
  leftWall.receiveShadow = true;
  walls.push(leftWall);
  
  const rightWall = new THREE.Mesh(sideWallGeometry, material);
  rightWall.position.set(15, wallHeight/2, 0);
  rightWall.castShadow = true;
  rightWall.receiveShadow = true;
  walls.push(rightWall);
  
  return walls;
}

function createGridCeiling(height: number): THREE.Group {
  const ceilingGroup = new THREE.Group();
  
  // Use instanced mesh for ceiling lights
  if (USE_INSTANCING) {
    const frameGeometry = new THREE.BoxGeometry(1, 0.08, 1, 1, 1, 1);
    const frameMaterial = new THREE.MeshStandardMaterial({
      color: 0x2b1b3a,
      roughness: 0.9,
      metalness: 0.1
    });
    const panelGeometry = sharedGeometries.panel;
    const panelMaterial = new THREE.MeshStandardMaterial({
      color: 0xb76ee5,
      emissive: 0xb76ee5,
      emissiveIntensity: EMISSIVE_INTENSITY
    });
    
    const gridSize = isMobile() ? 2 : 5;
    const totalLights = gridSize * gridSize;
    
    const frameInstances = new THREE.InstancedMesh(frameGeometry, frameMaterial, totalLights);
    const panelInstances = new THREE.InstancedMesh(panelGeometry, panelMaterial, totalLights);
    
    const matrix = new THREE.Matrix4();
    const spacing = 29.8 / (gridSize + 1);
    const startX = -29.8/2 + spacing;
    const startZ = startX;
    
    let instanceCount = 0;
    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize; j++) {
        if (isMobile() && (i + j) % 2 === 0) continue;
        
        matrix.makeTranslation(
          startX + i * spacing,
          height - 0.15,
          startZ + j * spacing
        );
        frameInstances.setMatrixAt(instanceCount, matrix);
        
        matrix.makeRotationX(Math.PI / 2);
        matrix.setPosition(
          startX + i * spacing,
          height - 0.25, // Lowered further to prevent clipping
          startZ + j * spacing
        );
        panelInstances.setMatrixAt(instanceCount, matrix);
        
        instanceCount++;
      }
    }
    
    frameInstances.instanceMatrix.needsUpdate = true;
    panelInstances.instanceMatrix.needsUpdate = true;
    
    // Main ceiling
    const ceilingGeometry = new THREE.PlaneGeometry(29.8, 29.8);
    const ceilingMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x2b1b3a,
      roughness: 0.9,
      metalness: 0.1,
      emissive: 0x2b1b3a,
      emissiveIntensity: 0.1
    });
    const ceiling = new THREE.Mesh(ceilingGeometry, ceilingMaterial);
    ceiling.rotation.x = Math.PI / 2;
    ceiling.position.y = height - 0.01;
    ceiling.receiveShadow = false;
    ceilingGroup.add(ceiling);
    
    ceilingGroup.add(frameInstances, panelInstances);
  } else {
    // Main ceiling
    const ceilingGeometry = new THREE.PlaneGeometry(29.8, 29.8);
    const ceilingMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x2b1b3a,
      roughness: 0.9,
      metalness: 0.1,
      emissive: 0x2b1b3a,
      emissiveIntensity: 0.1
    });
    const ceiling = new THREE.Mesh(ceilingGeometry, ceilingMaterial);
    ceiling.rotation.x = Math.PI / 2;
    ceiling.position.y = height - 0.01;
    ceiling.receiveShadow = false;
    ceilingGroup.add(ceiling);

    // Reduce number of lights on mobile
    const gridSize = isMobile() ? 3 : 5;
    const spacing = 29.8 / (gridSize + 1);
    const startX = -29.8/2 + spacing;
    const startZ = startX;

    // Create grid of lights with reduced complexity for mobile
    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize; j++) {
        if (isMobile() && (i + j) % 2 === 0) continue;

        const isAlternating = (i + j) % 2 === 0;
        
        // Frame with matching wall color
        const frameGeometry = new THREE.BoxGeometry(1, 0.08, 1);
        const frameMaterial = new THREE.MeshStandardMaterial({
          color: 0x2b1b3a,
          roughness: 0.9,
          metalness: 0.1
        });
        const frame = new THREE.Mesh(frameGeometry, frameMaterial);
        frame.position.set(
          startX + i * spacing,
          height - 0.15,
          startZ + j * spacing
        );
        ceilingGroup.add(frame);

        // Light panels with lighter purple
        const panelGeometry = new THREE.PlaneGeometry(0.8, 0.8);
        const panelMaterial = new THREE.MeshStandardMaterial({
          color: isAlternating ? 0xb76ee5 : 0xffffff,
          emissive: isAlternating ? 0xb76ee5 : 0xffffff,
          emissiveIntensity: EMISSIVE_INTENSITY
        });
        const panel = new THREE.Mesh(panelGeometry, panelMaterial);
        panel.rotation.x = Math.PI / 2;
        panel.position.set(
          startX + i * spacing,
          height - 0.25, // Lowered further to prevent clipping
          startZ + j * spacing
        );
        ceilingGroup.add(panel);
      }
    }
  }

  return ceilingGroup;
}

async function createComputerRows(): Promise<THREE.Group> {
  const rowsGroup = new THREE.Group();
  
  // Create two rows of computers back to back
  const computersPerRow = 6;
  const spacing = 2;
  const rowSpacing = 4;
  
  for (let row = 0; row < 2; row++) {
    for (let i = 0; i < computersPerRow; i++) {
      const desk = createGamingDesk();
      const deskX = (i - (computersPerRow - 1) / 2) * spacing;
      const deskZ = row === 0 ? -rowSpacing/2 : rowSpacing/2;
      desk.position.set(deskX, 0, deskZ);
      desk.rotation.y = row === 0 ? 0 : Math.PI;
      rowsGroup.add(desk);
      
      // Create retro computer
      const computerIndex = row * computersPerRow + i;
      const computer = await createRetroComputer();
      
      // Position the computer on the desk
      const computerX = deskX;
      const computerY = 1.5; // Place it higher on the desk
      const computerZ = row === 0 ? deskZ - 0.3 : deskZ + 0.3; // Adjusted position
      computer.position.set(computerX, computerY, computerZ);
      
      // Set rotation so computers face inward toward center aisle
      // First row computers face forward, second row computers face backward
      if (row === 0) {
        computer.rotation.y = 0; // First row faces forward (toward positive Z)
      } else {
        computer.rotation.y = Math.PI; // Second row faces backward (toward negative Z)
      }
      
      rowsGroup.add(computer);
      
      // Remove the existing keyboard since we're using the model
      const existingKeyboard = desk.children.find(child => 
        child instanceof THREE.Mesh && 
        child.geometry instanceof THREE.BoxGeometry && 
        child.position.y === 0.825
      );
      if (existingKeyboard) {
        desk.remove(existingKeyboard);
      }
      
      // Get configuration for this computer
      const config = DEFAULT_PC_CONFIG[computerIndex.toString()];
      if (config) {
        const label = `Press E to visit ${config.name}`;
        
        // Register with global interaction system
        if (window.registerInteractiveObject) {
          window.registerInteractiveObject(computer, config.url, label);
        }
        
        // Also keep in local array for backward compatibility
        window.interactiveObjects.push({
          mesh: computer,
          url: config.url,
          label: label,
          interactionDistance: 1.5
        });
      }
    }
  }
  
  return rowsGroup;
}

function createGamingDesk(): THREE.Group {
  const deskGroup = new THREE.Group();
  
  // Gaming desk (wider and deeper than before)
  const deskGeometry = new THREE.BoxGeometry(2.2, 0.8, 1.4);
  const deskMaterial = new THREE.MeshStandardMaterial({ 
    color: 0x1a1a1a,
    roughness: 0.7,
    metalness: 0.3
  });
  const desk = new THREE.Mesh(deskGeometry, deskMaterial);
  desk.position.y = 0.4;
  desk.castShadow = true;
  desk.receiveShadow = true;
  deskGroup.add(desk);
  
  // We're removing the mousepad and keyboard since they'll be replaced by the retro PC model
  
  return deskGroup;
}

function createGamingMonitor(): THREE.Group {
  const monitorGroup = new THREE.Group();
  
  // Monitor screen (ultrawide)
  const screenGeometry = new THREE.BoxGeometry(1.2, 0.5, 0.05);
  const screenMaterial = new THREE.MeshStandardMaterial({ 
    color: 0x000000,
    emissive: 0x00ff00,
    emissiveIntensity: 0.2
  });
  const screen = new THREE.Mesh(screenGeometry, screenMaterial);
  monitorGroup.add(screen);
  
  return monitorGroup;
}

function createArcadeMachines(): THREE.Group {
  const arcadeGroup = new THREE.Group();
  
  // Create arcade machines along the walls
  const arcadeCount = 5;
  const spacing = 5;

  // Helper function to create a poster with an image
  const createPoster = async (imageUrl: string): Promise<THREE.Group> => {
    const loader = new GLTFLoader();
    const gltf = await new Promise<GLTF>((resolve, reject) => {
      loader.load(
        '/assets/poster.glb',
        resolve,
        undefined,
        reject
      );
    });

    const posterModel = gltf.scene;
    posterModel.scale.set(0.5, 0.5, 0.5);
    // Rotate 90 degrees around Z axis to make it landscape
    posterModel.rotation.z = Math.PI / 2;

    // Find the poster surface/plane in the model to apply the texture
    posterModel.traverse((child: THREE.Object3D) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;

        if (child.material) {
          // Load and apply the game image texture
          const textureLoader = new THREE.TextureLoader();
          textureLoader.load(imageUrl, (texture) => {
            texture.colorSpace = THREE.SRGBColorSpace;
            // Fix mirroring issue by flipping the texture horizontally
            texture.repeat.set(-1, 1);
            texture.offset.set(1, 0);

            if (Array.isArray(child.material)) {
              child.material.forEach(mat => {
                mat.map = texture;
                mat.emissiveMap = texture;
                mat.emissive = new THREE.Color(0xffffff);
                mat.emissiveIntensity = 0.5;
                mat.needsUpdate = true;
              });
            } else {
              child.material.map = texture;
              child.material.emissiveMap = texture;
              child.material.emissive = new THREE.Color(0xffffff);
              child.material.emissiveIntensity = 0.5;
              child.material.needsUpdate = true;
            }
          });
        }
      }
    });

    return posterModel;
  };

  // Helper function to create a generic gaming poster
  const createGenericPoster = async (index: number): Promise<THREE.Group> => {
    const loader = new GLTFLoader();
    const gltf = await new Promise<GLTF>((resolve, reject) => {
      loader.load(
        '/assets/poster.glb',
        resolve,
        undefined,
        reject
      );
    });

    const posterModel = gltf.scene;
    posterModel.scale.set(0.5, 0.5, 0.5);
    // Rotate 90 degrees around Z axis to make it landscape
    posterModel.rotation.z = Math.PI / 2;

    // Different colors for different posters
    const colors = [
      0xff3333, // Red
      0x3333ff, // Blue
      0x33ff33, // Green
      0xffff33, // Yellow
      0xff33ff  // Purple
    ];

    // Different text for different posters
    const texts = ['GAME ON', 'PLAY NOW', 'HIGH SCORE', 'LEVEL UP', 'GAME OVER'];

    // Create canvas texture for generic poster
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const context = canvas.getContext('2d');

    if (context) {
      // Fill background
      context.fillStyle = '#000000';
      context.fillRect(0, 0, canvas.width, canvas.height);

      // Add text
      context.fillStyle = '#ffffff';
      context.font = 'bold 64px Arial';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(texts[index % texts.length], canvas.width / 2, canvas.height / 2);

      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      // Fix mirroring issue for generic posters too
      texture.repeat.set(-1, 1);
      texture.offset.set(1, 0);

      // Apply texture to the poster model
      posterModel.traverse((child: THREE.Object3D) => {
        if (child instanceof THREE.Mesh && child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(mat => {
              mat.map = texture;
              mat.emissiveMap = texture;
              mat.emissive = new THREE.Color(colors[index % colors.length]);
              mat.emissiveIntensity = 0.5;
              mat.needsUpdate = true;
            });
          } else {
            child.material.map = texture;
            child.material.emissiveMap = texture;
            child.material.emissive = new THREE.Color(colors[index % colors.length]);
            child.material.emissiveIntensity = 0.5;
            child.material.needsUpdate = true;
          }
        }
      });
    }

    return posterModel;
  };

  for (let i = 0; i < arcadeCount; i++) {
    // Place arcade on right wall
    const rightConfig = ARCADE_CONFIG.right[i];
    
    createArcadeMachine(rightConfig?.image).then(arcade => {
      // Position and rotate the arcade machine against the right wall
      arcade.position.set(14.3, 0, -10 + i * spacing);
      arcade.rotation.y = -Math.PI / 2 + Math.PI / 2; // Rotate 90 degrees clockwise from current position
      arcadeGroup.add(arcade);
      
      // Add poster above arcade machine if there's an image
      if (rightConfig?.image) {
        createPoster(rightConfig.image).then(poster => {
          poster.position.set(14.8, 4.5, -10 + i * spacing); // Position on the wall above the arcade
          poster.rotation.y = Math.PI / 2; // Rotate to face inside the room
          arcadeGroup.add(poster);
        }).catch(error => {
          // Create generic poster as fallback
          createGenericPoster(i).then(fallbackPoster => {
            fallbackPoster.position.set(14.8, 4.5, -10 + i * spacing);
            fallbackPoster.rotation.y = Math.PI / 2;
            arcadeGroup.add(fallbackPoster);
          });
        });
      }
      
      // Make arcade interactive only if it has a URL
      if (rightConfig?.url) {
        const label = `Press E to play ${rightConfig.name}`;
        
        // Register with global interaction system
        if (window.registerInteractiveObject) {
          window.registerInteractiveObject(arcade, rightConfig.url, label);
        }
        
        // Also keep in local array for backward compatibility
        window.interactiveObjects.push({
          mesh: arcade,
          url: rightConfig.url,
          label: label,
          interactionDistance: 2
        });
      }
    });
    
    // Place arcade on left wall
    const leftConfig = ARCADE_CONFIG.left[i];
    
    createArcadeMachine(leftConfig?.image).then(arcade2 => {
      // Position and rotate the arcade machine against the left wall
      arcade2.position.set(-14.3, 0, -10 + i * spacing);
      arcade2.rotation.y = Math.PI / 2 + Math.PI / 2; // Rotate 90 degrees clockwise from current position
      arcadeGroup.add(arcade2);
      
      // Add poster above arcade machine if there's an image
      if (leftConfig?.image) {
        createPoster(leftConfig.image).then(poster => {
          poster.position.set(-14.8, 4.5, -10 + i * spacing); // Position on the wall above the arcade
          poster.rotation.y = -Math.PI / 2; // Rotate to face inside the room
          arcadeGroup.add(poster);
        }).catch(error => {
          // Create generic poster as fallback
          createGenericPoster(i).then(fallbackPoster => {
            fallbackPoster.position.set(-14.8, 4.5, -10 + i * spacing);
            fallbackPoster.rotation.y = -Math.PI / 2;
            arcadeGroup.add(fallbackPoster);
          });
        });
      }
      
      // Make arcade interactive only if it has a URL
      if (leftConfig?.url) {
        const label = `Press E to play ${leftConfig.name}`;
        
        // Register with global interaction system
        if (window.registerInteractiveObject) {
          window.registerInteractiveObject(arcade2, leftConfig.url, label);
        }
        
        // Also keep in local array for backward compatibility
        window.interactiveObjects.push({
          mesh: arcade2,
          url: leftConfig.url,
          label: label,
          interactionDistance: 2
        });
      }
    });
  }
  
  return arcadeGroup;
}

// Modified to return a Promise<THREE.Group> since loading is asynchronous
async function createArcadeMachine(gameImageUrl?: string): Promise<THREE.Group> {
  const arcadeGroup = new THREE.Group();
  arcadeGroup.name = 'arcadeCabinet';
  
  try {
    const loader = new GLTFLoader();
    
    const gltf = await new Promise<THREE.Group>((resolve, reject) => {
      loader.load(
        '/assets/arcade_machine.glb',
        (gltf) => {
          resolve(gltf.scene);
        },
        undefined,
        reject
      );
    });
    
    // Scale the arcade machine model appropriately
    gltf.scale.set(0.5, 0.5, 0.5);
    
    // Make sure all materials and meshes are properly visible
    gltf.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
        
        if (child.material) {
          // Ensure materials are visible
          if (Array.isArray(child.material)) {
            child.material.forEach(mat => {
              mat.transparent = false;
              mat.opacity = 1;
              
              // Set proper color space for textures
              if (mat.map) {
                mat.map.colorSpace = THREE.SRGBColorSpace;
              }
            });
          } else {
            child.material.transparent = false;
            child.material.opacity = 1;
            
            // Set proper color space for textures
            if (child.material.map) {
              child.material.map.colorSpace = THREE.SRGBColorSpace;
            }
            
            // If this is the screen and we have a game image URL, apply it
            if (child.name.toLowerCase().includes('screen') && gameImageUrl) {
              loadCachedTexture(gameImageUrl).then(texture => {
                child.material.map = texture;
                child.material.emissiveMap = texture;
                child.material.emissive = new THREE.Color(0xffffff);
                child.material.emissiveIntensity = 0.8;
                child.material.needsUpdate = true;
              });
            }
          }
        }
      }
    });
    
    arcadeGroup.add(gltf);
    return arcadeGroup;
    
  } catch (error) {
    // Create a fallback arcade machine using our primitive-based approach
    const { createArcadePrimitive } = await import('./ArcadePrimitive');
    
    // Generate a random color scheme for variety
    const colorSchemes = [
      { cabinet: '#333333', screen: '#4444ff', accent: '#ffcc00' }, // Classic blue/yellow
      { cabinet: '#662222', screen: '#ff4444', accent: '#ffff00' }, // Red/yellow
      { cabinet: '#222266', screen: '#44ff44', accent: '#ff00ff' }, // Blue/green/pink
      { cabinet: '#226622', screen: '#ff44ff', accent: '#00ffff' }, // Green/pink/cyan
      { cabinet: '#664422', screen: '#44ffff', accent: '#ff4444' }  // Brown/cyan/red
    ];
    
    const randomScheme = colorSchemes[Math.floor(Math.random() * colorSchemes.length)];
    
    // Create the arcade machine with the random color scheme and game image
    const arcade = createArcadePrimitive(
      randomScheme.cabinet,
      randomScheme.screen,
      randomScheme.accent,
      gameImageUrl
    );
    
    arcadeGroup.add(arcade);
    return arcadeGroup;
  }
}

function createPlayStationCorner(): THREE.Group {
  const cornerGroup = new THREE.Group();
  
  // TV Stand
  const standGeometry = new THREE.BoxGeometry(2, 0.5, 1);
  const standMaterial = new THREE.MeshStandardMaterial({
    color: 0x1a1a1a
  });
  const stand = new THREE.Mesh(standGeometry, standMaterial);
  stand.position.y = 0.25;
  cornerGroup.add(stand);
  
  // TV
  const tvGeometry = new THREE.BoxGeometry(1.8, 1, 0.1);
  const tvMaterial = new THREE.MeshStandardMaterial({
    color: 0x000000,
    emissive: 0x0000ff,
    emissiveIntensity: 0.2
  });
  const tv = new THREE.Mesh(tvGeometry, tvMaterial);
  tv.position.set(0, 1.5, 0);
  cornerGroup.add(tv);
  
  // PlayStation console
  const consoleGeometry = new THREE.BoxGeometry(0.4, 0.1, 0.3);
  const consoleMaterial = new THREE.MeshStandardMaterial({
    color: 0x1a1a1a,
    metalness: 0.8
  });
  const console = new THREE.Mesh(consoleGeometry, consoleMaterial);
  console.position.set(0, 0.55, 0);
  cornerGroup.add(console);
  
  // Couch
  const couchGeometry = new THREE.BoxGeometry(2, 0.8, 0.8);
  const couchMaterial = new THREE.MeshStandardMaterial({
    color: 0x000000
  });
  const couch = new THREE.Mesh(couchGeometry, couchMaterial);
  couch.position.set(0, 0.4, 2);
  cornerGroup.add(couch);
  
  return cornerGroup;
}

function createCafeCounter(): THREE.Group {
  const counterGroup = new THREE.Group();
  
  // Main counter
  const counterGeometry = new THREE.BoxGeometry(4, 1.2, 1);
  const counterMaterial = new THREE.MeshStandardMaterial({
    color: 0x2b1b3a, // Dark purple
    metalness: 0.3
  });
  const counter = new THREE.Mesh(counterGeometry, counterMaterial);
  counter.position.y = 0.6;
  counterGroup.add(counter);
  
  // Display case for snacks
  const displayGeometry = new THREE.BoxGeometry(3, 0.8, 0.6);
  const displayMaterial = new THREE.MeshStandardMaterial({
    color: 0x000000,
    metalness: 0.8,
    transparent: true,
    opacity: 0.7
  });
  const display = new THREE.Mesh(displayGeometry, displayMaterial);
  display.position.set(0, 1.2, 0);
  counterGroup.add(display);
  
  // Register
  const registerGeometry = new THREE.BoxGeometry(0.4, 0.3, 0.3);
  const registerMaterial = new THREE.MeshStandardMaterial({
    color: 0x000000
  });
  const register = new THREE.Mesh(registerGeometry, registerMaterial);
  register.position.set(-1.5, 1.2, 0);
  counterGroup.add(register);
  
  return counterGroup;
}

function createBaristaCounter(): Promise<THREE.Group> {
  const counterGroup = new THREE.Group();
  counterGroup.name = 'baristaCounter';

  return new Promise((resolve, reject) => {
    // Load the sci-fi counter GLB model
    const loader = new GLTFLoader();
    loader.load(
      '/assets/sci-fi_counter_top.glb',
      (gltf) => {
        const model = gltf.scene;
        
        // Make the model even smaller
        model.scale.set(0.015, 0.015, 0.015);
        
        // Rotate clockwise twice
        model.rotation.y = Math.PI;
        
        // Add the model to the group
        counterGroup.add(model);
        
        // Make sure materials and meshes are properly visible
        model.traverse((child: THREE.Object3D) => {
          if (child instanceof THREE.Mesh) {
            child.castShadow = true;
            child.receiveShadow = true;
            
            if (child.material) {
              // Ensure materials are visible
              if (Array.isArray(child.material)) {
                child.material.forEach(mat => {
                  mat.transparent = mat.transparent || false;
                  mat.opacity = 1;
                  
                  // Set proper color space for textures
                  if (mat.map) {
                    mat.map.colorSpace = THREE.SRGBColorSpace;
                  }
                });
              } else {
                child.material.transparent = child.material.transparent || false;
                child.material.opacity = 1;
                
                // Set proper color space for textures
                if (child.material.map) {
                  child.material.map.colorSpace = THREE.SRGBColorSpace;
                }
              }
            }
          }
        });

        resolve(counterGroup);
      },
      undefined,
      (error) => {
        reject(error);
      }
    );
  });
}

function createCoffeeCup(): THREE.Group {
  const cupGroup = new THREE.Group();
  
  // Reduce geometry complexity on mobile
  const cupGeometry = new THREE.CylinderGeometry(0.1, 0.07, 0.2, GEOMETRY_SEGMENTS);
  const cupMaterial = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    roughness: 0.2
  });
  const cup = new THREE.Mesh(cupGeometry, cupMaterial);
  cupGroup.add(cup);
  
  // Reduce handle complexity on mobile
  const handleGeometry = new THREE.TorusGeometry(0.05, 0.01, 6, GEOMETRY_SEGMENTS, Math.PI);
  const handle = new THREE.Mesh(handleGeometry, cupMaterial);
  handle.position.set(0.08, 0, 0);
  handle.rotation.y = Math.PI / 2;
  cupGroup.add(handle);
  
  return cupGroup;
}

function addGamingDecorations(cafeGroup: THREE.Group, wallHeight: number): void {
  // Add LED strips along the walls
  const ledGeometry = new THREE.BoxGeometry(0.1, 0.1, 28); // Reduced length to prevent corner overlap
  const ledMaterial = new THREE.MeshStandardMaterial({
    color: 0x9932cc,
    emissive: 0x9932cc,
    emissiveIntensity: 0.5
  });
  
  // LED strips on side walls (moved slightly inward)
  const leftLED = new THREE.Mesh(ledGeometry, ledMaterial);
  leftLED.position.set(-14.8, wallHeight - 0.5, 0);
  cafeGroup.add(leftLED);
  
  const rightLED = new THREE.Mesh(ledGeometry, ledMaterial);
  rightLED.position.set(14.8, wallHeight - 0.5, 0);
  cafeGroup.add(rightLED);
  
  // LED strips on back wall (reduced width to prevent corner overlap)
  const backLED = new THREE.Mesh(
    new THREE.BoxGeometry(28, 0.1, 0.1),
    ledMaterial
  );
  backLED.position.set(0, wallHeight - 0.5, -14.8);
  cafeGroup.add(backLED);
  
  // Removed gaming posters - we're only keeping the posters above arcade machines
}

function createGamingPoster(type: number): THREE.Mesh {
  const posterGeometry = new THREE.PlaneGeometry(1.5, 2);
  
  let emissiveColor;
  switch (type) {
    case 0:
      emissiveColor = 0xff0000; // Red
      break;
    case 1:
      emissiveColor = 0x00ff00; // Green
      break;
    case 2:
      emissiveColor = 0x0000ff; // Blue
      break;
    default:
      emissiveColor = 0x9932cc; // Purple
  }
  
  const posterMaterial = new THREE.MeshStandardMaterial({ 
    color: 0x000000,
    emissive: emissiveColor,
    emissiveIntensity: 0.5,
    side: THREE.DoubleSide
  });
  
  return new THREE.Mesh(posterGeometry, posterMaterial);
}

// New function to create a retro computer model
async function createRetroComputer(): Promise<THREE.Group> {
  const computerGroup = new THREE.Group();
  computerGroup.name = 'retroComputer';
  
  // Load the GLB model with error handling
  try {
    const loader = new GLTFLoader();
    
    const gltf = await new Promise<THREE.Group>((resolve, reject) => {
      loader.load(
        '/assets/retro_pc.glb',
        (gltf) => {
          resolve(gltf.scene);
        },
        undefined,
        reject
      );
    });
    
    // Scale the model to be much smaller (10 times smaller than before)
    gltf.scale.set(0.004, 0.004, 0.004);
    
    // Make sure all materials and meshes are properly visible
    gltf.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
        
        if (child.material) {
          // Ensure materials are visible
          if (Array.isArray(child.material)) {
            child.material.forEach(mat => {
              mat.transparent = false;
              mat.opacity = 1;
              mat.side = THREE.DoubleSide;
              
              // Set proper color space for textures
              if (mat.map) {
                mat.map.colorSpace = THREE.SRGBColorSpace;
              }
            });
          } else {
            child.material.transparent = false;
            child.material.opacity = 1;
            child.material.side = THREE.DoubleSide;
            
            // Set proper color space for textures
            if (child.material.map) {
              child.material.map.colorSpace = THREE.SRGBColorSpace;
            }
          }
        }
      }
    });
    
    computerGroup.add(gltf);
    return computerGroup;
  
  } catch (error) {
    throw error;
  }
}

// Modify createAICafeSign to be more mobile-friendly
function createAICafeSign(): THREE.Group {
  const signGroup = new THREE.Group();
  
  // Use shared materials and simplified geometry
  const signGeometry = new THREE.BoxGeometry(8, 1.5, 0.1, 1, 1, 1);
  const signMaterial = sharedMaterials.wall.clone();
  signMaterial.emissiveIntensity = EMISSIVE_INTENSITY;
  
  const signPanel = new THREE.Mesh(signGeometry, signMaterial);
  signGroup.add(signPanel);
  
  // Load and optimize texture
  loadCachedTexture('/assets/AI CAFE.png').then(texture => {
    const imageGeometry = new THREE.PlaneGeometry(7.5, 1.2, 1, 1);
    const imageMaterial = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      side: THREE.DoubleSide
    });
    
    const imagePlane = new THREE.Mesh(imageGeometry, imageMaterial);
    imagePlane.position.set(0, 0, 0.06);
    signGroup.add(imagePlane);
  });
  
  // Skip point light on mobile
  if (!isMobile()) {
    const pointLight = new THREE.PointLight(0xffffff, 0.7, 3); // Reduced intensity
    pointLight.position.set(0, 0, 0.5);
    signGroup.add(pointLight);
  }
  
  // Simplified glow effect
  const glowGeometry = new THREE.BoxGeometry(8.2, 1.7, 0.05, 1, 1, 1);
  const glowMaterial = sharedMaterials.emissive.clone();
  glowMaterial.transparent = true;
  glowMaterial.opacity = isMobile() ? 0.2 : 0.4;
  
  const glow = new THREE.Mesh(glowGeometry, glowMaterial);
  glow.position.set(0, 0, -0.05);
  signGroup.add(glow);
  
  return signGroup;
}

// Function to setup the interaction system
function setupInteractionSystem(): void {
  // Create a text element for interaction prompts
  const promptElement = document.createElement('div');
  promptElement.style.position = 'absolute';
  promptElement.style.bottom = '20%';
  promptElement.style.left = '50%';
  promptElement.style.transform = 'translateX(-50%)';
  promptElement.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
  promptElement.style.color = 'white';
  promptElement.style.padding = '10px 20px';
  promptElement.style.borderRadius = '5px';
  promptElement.style.fontFamily = 'Arial, sans-serif';
  promptElement.style.fontSize = '18px';
  promptElement.style.display = 'none';
  promptElement.style.zIndex = '1000';
  document.body.appendChild(promptElement);
  
  // Variables to track interaction state
  let currentInteractiveObject: InteractiveObject | null = null;
  
  // Function to check for nearby interactive objects
  const checkInteractions = (playerPosition: THREE.Vector3) => {
    let closestObject: InteractiveObject | null = null;
    let closestDistance = Infinity;
    
    window.interactiveObjects.forEach(obj => {
      // Get world position of the object
      const objWorldPos = new THREE.Vector3();
      obj.mesh.getWorldPosition(objWorldPos);
      
      // Calculate distance to player
      const distance = playerPosition.distanceTo(objWorldPos);
      
      // Check if within interaction distance and closer than any previous object
      if (distance < obj.interactionDistance && distance < closestDistance) {
        closestObject = obj;
        closestDistance = distance;
      }
    });
    
    // For dance machine specifically, check direct distance to its fixed position
    const danceMachinePos = new THREE.Vector3(-20, 1.5, -12);
    const distanceToDanceMachine = playerPosition.distanceTo(danceMachinePos);
    
    if (distanceToDanceMachine < 1) { // Reduced from 15 to 5 units
      // Find dance machine in interactiveObjects
      const danceMachineInteraction = window.interactiveObjects.find(obj => 
        obj.mesh.name === 'danceMachineInteraction' || 
        (obj.url && obj.url === '#dance')
      );
      
      if (danceMachineInteraction) {
        // Update the interaction distance to be smaller
        danceMachineInteraction.interactionDistance = 1.5; // Consistently small detection radius
        
        // Get dance state for better prompt text
        const isDancing = window.character?.controller?.getIsDancing?.() || false;
        
        // Update label based on dance state
        danceMachineInteraction.label = isDancing ? "Press E to Stop Dancing" : "Press E to Dance!";
        
        closestObject = danceMachineInteraction;
        closestDistance = distanceToDanceMachine;
      }
    }
    
    // Update current interactive object
    if (closestObject !== currentInteractiveObject) {
      currentInteractiveObject = closestObject;
      
      if (currentInteractiveObject) {
        // Show prompt with device-specific text
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const actionText = isMobile ? 'Tap E' : 'Press E';
        
        // Fix type issue by ensuring currentInteractiveObject is properly typed
        const label = (currentInteractiveObject as InteractiveObject).label;
        promptElement.textContent = label.replace('Press E', actionText);
        promptElement.style.display = 'block';
        
        // Add special highlight for dance machine
        if (currentInteractiveObject.url === "#dance") {
          promptElement.style.backgroundColor = 'rgba(153, 50, 204, 0.8)'; // Purple highlight
          promptElement.style.border = '2px solid white';
          promptElement.style.fontSize = '20px'; // Slightly larger
          promptElement.style.padding = '12px 25px';
        } else {
          // Reset to default for other interactions
          promptElement.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
          promptElement.style.border = 'none';
          promptElement.style.fontSize = '18px';
          promptElement.style.padding = '10px 20px';
        }
      } else {
        // Hide prompt
        promptElement.style.display = 'none';
      }
    }
  };
  
  // Function to handle interaction (E key press)
  const handleInteraction = () => {
    if (currentInteractiveObject) {
      // Track the interaction event
      if ((window as any).gtag) {
        (window as any).gtag('event', 'arcade_interaction', {
          'event_category': 'game',
          'event_label': currentInteractiveObject.url,
          'value': 1
        });
      }

      // Special handling for dance machine
      if (currentInteractiveObject.mesh.name === 'danceMachineInteraction' || 
          (currentInteractiveObject.url && currentInteractiveObject.url === '#dance')) {
        
        // Get the character controller from the window object
        if (window.character?.controller) {
          // Check if already dancing to toggle dancing on/off
          const isDancing = window.character.controller.getIsDancing?.() || false;
          
          if (isDancing) {
            window.character.controller.stopDancing();
            promptElement.textContent = "Press E to Dance!";
          } else {
            promptElement.textContent = "Press E to Stop Dancing";
            window.character.controller.startDancing();
          }
        }
        return;
      }

      // Special handling for vending machine - use custom handler
      if (currentInteractiveObject.onInteract) {
        currentInteractiveObject.onInteract();
        return;
      }

      // Special handling for buymeacoffee links - open in new tab only
      if (currentInteractiveObject.url.includes('buymeacoffee.com')) {
        window.open(currentInteractiveObject.url, '_blank');
        return; // Exit early to prevent iframe from opening
      }
      
      // For all other interactions, use the iframe overlay
      if (typeof window.handleInteraction === 'function') {
        window.handleInteraction();
      }
    }
  };
  
  // Add event listener for key press
  document.addEventListener('keydown', (event) => {
    if (event.key.toLowerCase() === 'e') {
      handleInteraction();
    }
  });
  
  // Add this function to the animation loop or player update function
  window.checkInteractiveObjects = (playerPosition: THREE.Vector3) => {
    checkInteractions(playerPosition);
  };
  
  // Expose the handleInteraction function globally for mobile devices
  (window as any).handleArcadeInteraction = handleInteraction;
}

// Create a separate function for coffee machine creation
function createCoffeeMachine(position: THREE.Vector3, rotation: number): Promise<THREE.Group> {
  return new Promise((resolve, reject) => {
    const loader = new GLTFLoader();
    loader.load(
      '/assets/coffee_machine.glb',
      (gltf) => {
        const coffeeMachine = gltf.scene;
        coffeeMachine.scale.set(0.5, 0.5, 0.5);
        coffeeMachine.position.copy(position);
        coffeeMachine.rotation.y = rotation;
        
        coffeeMachine.traverse((child: THREE.Object3D) => {
          if (child instanceof THREE.Mesh) {
            child.castShadow = true;
            child.receiveShadow = true;
          }
        });
        
        resolve(coffeeMachine);
      },
      undefined,
      (error) => {
        // Create a fallback primitive shape if the model fails to load
        const fallbackMachine = createCoffeeMachinePrimitive({
          bodyColor: '#2a2a2a',
          accentColor: '#ff0000',
          metalColor: '#d0d0d0',
          screenColor: '#00ff00'
        });
        fallbackMachine.scale.set(1.2, 1.2, 1.2);
        fallbackMachine.position.copy(position);
        fallbackMachine.rotation.y = rotation;
        resolve(fallbackMachine);
      }
    );
  });
} 